import React, { useState } from 'react';
import { View, useWindowDimensions } from 'react-native';

import { useT } from '@transifex/react';
import axios from 'axios';

import { AuthFooter } from '../components/AuthFooter';
import { AuthLayout } from '../components/AuthLayout';
import { BloomButton } from '../components/BloomButton';
import { InlineError } from '../components/InlineError';
import { Input } from '../components/Input';
import { Logo } from '../components/Logo';
import ThemedText from '../components/ThemedText';
import { ROUTES, ERROR_MESSAGES } from '../utils/constants';
import { screenTestId } from '../utils/testId';
import { validateEmail } from '../services/validationService';
import { sendLoginLink } from '../api/magicLink';
import { setPendingMagicLinkEmail } from '../services/magicLinkEmailMemo';
import { analytics } from '../utils/analytics';
import { useLoginRouter } from '../hooks/useLoginRouter';

const tid = screenTestId('MagicLink');

export default function MagicLinkScreen() {
  const router = useLoginRouter();
  const { width } = useWindowDimensions();
  const t = useT();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isDesktop = width >= 768;

  const handleSubmit = async () => {
    setEmailError(null);
    setError(null);

    const emailValidation = validateEmail(email);
    if (emailValidation) {
      setEmailError(emailValidation);
      return;
    }

    setLoading(true);
    analytics.trackEvent('magic_link_send_submitted');

    try {
      await sendLoginLink({ email });
      analytics.trackEvent('magic_link_send_succeeded');
      // Keep the email out of the URL — it's an identifier that would leak
      // to history, logs, Referer, and analytics auto-capture. Hand it off
      // via a short-lived in-memory memo instead.
      setPendingMagicLinkEmail(email);
      router.replace(ROUTES.MAGIC_LINK_SENT);
    } catch (err) {
      const rateLimited =
        axios.isAxiosError(err) &&
        err.response?.status === 403 &&
        err.response?.data?.reason === 'rate_limited';
      analytics.trackEvent('magic_link_send_failed', { rate_limited: rateLimited });
      setError(
        rateLimited
          ? ERROR_MESSAGES.EMAIL_LOGIN_LINK_RATE_LIMITED
          : ERROR_MESSAGES.EMAIL_LOGIN_LINK_FAILED,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.replace(ROUTES.LOGIN);
  };

  return (
    <AuthLayout>
      <View className='w-full'>
        <Logo />

        <View className='w-full'>
          <ThemedText
            type='titleHero'
            className='mb-2 font-bold'
            style={{ fontSize: 32, lineHeight: 40 }}
          >
            {t('Sign in without a password')}
          </ThemedText>
          <ThemedText
            type='bodyRegular'
            color='subtle'
            className={isDesktop ? 'mb-6' : 'mb-8'}
          >
            {t("Enter your email and we'll send you a link that signs you in — no password needed.")}
          </ThemedText>

          <InlineError message={error} testID={tid('error')} />

          <Input
            label={t('Email')}
            value={email}
            onChangeText={setEmail}
            placeholder={t('Enter your email address')}
            keyboardType='email-address'
            autoComplete='email'
            autoCorrect={false}
            spellCheck={false}
            maxLength={254}
            error={emailError || undefined}
            onSubmitEditing={handleSubmit}
            testID={tid('email-input')}
          />

          <View>
            <BloomButton
              text={t('Email me a sign in link')}
              onPress={handleSubmit}
              loading={loading}
              testID={tid('send-link-button')}
            />
          </View>

          <AuthFooter
            variant='secondary'
            onBackToLogin={handleBack}
            testID={tid('footer')}
          />
        </View>
      </View>
    </AuthLayout>
  );
}
